"use client";

import { ValuePropMathBurst } from "@/components/about/value-prop-math-burst";
import { ValuePropSourceIconButton } from "@/components/about/value-prop-source-icon-button";
import {
  BRAIN_SOURCES,
  type BrainSource,
  VALUE_PROP_AUTO_ROTATE_MS,
  VALUE_PROP_DEMO_APPROACH_MS,
  VALUE_PROP_DEMO_CURSOR_LEAD_MS,
  VALUE_PROP_DEMO_HOVER_MS,
  VALUE_PROP_DEMO_RELEASE_MS,
  VALUE_PROP_RIVE_POP_EASING,
  VALUE_PROP_RIVE_POP_FROM_SCALE,
  VALUE_PROP_RIVE_POP_MS,
  type ValuePropDemoClickPhase,
  nextClockwiseBrainSource,
} from "@/components/about/value-prop-utils";
import { DEFAULT_RIVE_STATE_MACHINE, RiveTile } from "@/components/feature/rive-tile";
import { SectionFrame } from "@/components/ui/section-frame";
import { useEffect, useState } from "react";

export type { BrainSource } from "@/components/about/value-prop-utils";
export {
  BRAIN_SOURCES,
  VALUE_PROP_AUTO_ROTATE_MS,
  VALUE_PROP_CLOCKWISE_ORDER,
  VALUE_PROP_DEMO_APPROACH_MS,
  VALUE_PROP_DEMO_CURSOR_LEAD_MS,
  VALUE_PROP_DEMO_HOVER_MS,
  VALUE_PROP_DEMO_PRESS_MS,
  VALUE_PROP_DEMO_RELEASE_MS,
  VALUE_PROP_DIMMED_OPACITY,
  VALUE_PROP_DIMMED_SCALE,
  VALUE_PROP_HOVER_OPACITY,
  VALUE_PROP_HOVER_SCALE,
  VALUE_PROP_ICON_ROCK_IDLE_DEG,
  VALUE_PROP_ICON_ROCK_PERIOD_S,
  VALUE_PROP_ICON_ROCK_SELECTED_DEG,
  VALUE_PROP_RIVE_POP_EASING,
  VALUE_PROP_RIVE_POP_FROM_SCALE,
  VALUE_PROP_RIVE_POP_MS,
  nextClockwiseBrainSource,
  riveSrcForBrainSource,
} from "@/components/about/value-prop-utils";

export function ValuePropSection() {
  const [source, setSource] = useState<BrainSource>("tik");
  const [userHasSelected, setUserHasSelected] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [demoTarget, setDemoTarget] = useState<BrainSource | null>(null);
  const [demoPhase, setDemoPhase] = useState<ValuePropDemoClickPhase>("idle");
  const leftSources = BRAIN_SOURCES.filter((entry) => entry.side === "left");
  const rightSources = BRAIN_SOURCES.filter((entry) => entry.side === "right");
  const activeLabel = BRAIN_SOURCES.find((entry) => entry.id === source)?.label ?? source;

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setPrefersReducedMotion(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  useEffect(() => {
    if (userHasSelected || prefersReducedMotion) {
      setDemoTarget(null);
      setDemoPhase("idle");
      return;
    }

    const next = nextClockwiseBrainSource(source);
    const approachAt = VALUE_PROP_AUTO_ROTATE_MS - VALUE_PROP_DEMO_CURSOR_LEAD_MS;

    // Clear any leftover release cursor from the previous cycle.
    const clearTimer = window.setTimeout(() => {
      setDemoTarget(null);
      setDemoPhase("idle");
    }, VALUE_PROP_DEMO_RELEASE_MS);

    const approachTimer = window.setTimeout(() => {
      setDemoTarget(next);
      setDemoPhase("approach");
    }, approachAt);

    const pressTimer = window.setTimeout(
      () => {
        setDemoPhase("press");
      },
      approachAt + VALUE_PROP_DEMO_APPROACH_MS + VALUE_PROP_DEMO_HOVER_MS
    );

    const clickTimer = window.setTimeout(() => {
      setSource(next);
      setDemoPhase("release");
    }, VALUE_PROP_AUTO_ROTATE_MS);

    return () => {
      window.clearTimeout(clearTimer);
      window.clearTimeout(approachTimer);
      window.clearTimeout(pressTimer);
      window.clearTimeout(clickTimer);
    };
  }, [source, userHasSelected, prefersReducedMotion]);

  const handleSelect = (id: BrainSource) => {
    setUserHasSelected(true);
    setDemoTarget(null);
    setDemoPhase("idle");
    setSource(id);
  };

  const demoPhaseFor = (id: BrainSource): ValuePropDemoClickPhase =>
    demoTarget === id ? demoPhase : "idle";

  return (
    <SectionFrame startColor="rgb(var(--accent-pink))" endColor="rgb(var(--accent-pink-deep))">
      <div className="relative">
        <img
          src="/images/marketing/home/value/brain-playmath.svg"
          alt=""
          aria-hidden="true"
          className="hidden md:block pointer-events-none select-none absolute top-0 right-0 w-80 md:w-[32rem] opacity-60 z-0"
          style={{ transform: "translateY(-20%) translateX(0%)" }}
        />
        <img
          src="/images/marketing/home/value/brain-junk.svg"
          alt=""
          aria-hidden="true"
          className="hidden md:block pointer-events-none select-none absolute top-1/2 left-0 w-48 md:w-[18rem] opacity-60 z-0"
          style={{ transform: "translateY(0%) translateX(-30%)" }}
        />

        <h2 className="text-2xl sm:text-5xl font-bold mb-8 sm:mb-10 text-center text-white relative z-10">
          This is your brain on...
        </h2>

        <div
          className="relative z-10 mx-auto flex w-full max-w-4xl items-center justify-center gap-3 sm:gap-6 md:gap-10"
          data-testid="value-prop-grid"
          role="radiogroup"
          aria-label="This is your brain on"
          data-value={source}
          data-auto-rotate={userHasSelected || prefersReducedMotion ? "false" : "true"}
          data-demo-phase={demoPhase}
          data-demo-target={demoTarget ?? ""}
        >
          <div
            className="flex shrink-0 flex-col items-center gap-4 sm:gap-6"
            data-testid="value-prop-icons-left"
          >
            {leftSources.map((entry) => (
              <ValuePropSourceIconButton
                key={entry.id}
                source={entry}
                selected={source === entry.id}
                onSelect={handleSelect}
                demoClickPhase={demoPhaseFor(entry.id)}
              />
            ))}
          </div>

          <div
            className="relative aspect-square w-full max-w-[14rem] sm:max-w-sm md:max-w-md overflow-visible"
            data-testid="value-prop-rive"
            aria-live="polite"
            aria-label={`Brain animation for ${activeLabel}`}
          >
            <ValuePropMathBurst active={source === "playmath"} />
            {/* Keep all three mounted so switches don't flash while a .riv reloads. */}
            {BRAIN_SOURCES.map((entry) => {
              const isActive = source === entry.id;
              return (
                <div
                  key={entry.id}
                  data-testid={`value-prop-rive-layer-${entry.id}`}
                  data-active={isActive ? "true" : "false"}
                  data-src={entry.riveSrc}
                  aria-hidden={isActive ? undefined : true}
                  className={[
                    "absolute inset-0 z-10 origin-center will-change-transform",
                    isActive ? "opacity-100" : "opacity-0 pointer-events-none",
                  ].join(" ")}
                  style={{
                    // Instant visibility swap; only the incoming layer springs up.
                    transform: `scale(${isActive ? 1 : VALUE_PROP_RIVE_POP_FROM_SCALE})`,
                    transition: isActive
                      ? `transform ${VALUE_PROP_RIVE_POP_MS}ms ${VALUE_PROP_RIVE_POP_EASING}`
                      : "none",
                  }}
                >
                  <RiveTile
                    src={entry.riveSrc}
                    // Known marketing .riv files use the editor default state machine.
                    stateMachines={DEFAULT_RIVE_STATE_MACHINE}
                    // Pause canvas playback when the user requests reduced motion.
                    active={isActive && !prefersReducedMotion}
                    className="absolute inset-0 h-full w-full"
                    ariaLabel={isActive ? `Brain animation for ${entry.label}` : undefined}
                  />
                </div>
              );
            })}
          </div>

          <div
            className="flex shrink-0 flex-col items-center gap-4 sm:gap-6"
            data-testid="value-prop-icons-right"
          >
            {rightSources.map((entry) => (
              <ValuePropSourceIconButton
                key={entry.id}
                source={entry}
                selected={source === entry.id}
                onSelect={handleSelect}
                demoClickPhase={demoPhaseFor(entry.id)}
              />
            ))}
          </div>
        </div>
      </div>
    </SectionFrame>
  );
}

export default ValuePropSection;
